import AddIcon from '@mui/icons-material/Add';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Button, IconButton, Menu, MenuItem } from '@mui/material';
import Widget from 'app/common/components/widgets/widget';
import cx from 'classnames';
import { singular } from 'pluralize';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import styled from 'styled-components';
import { PLACEHOLDER_KEY } from '../../constants';
import { getDiscriminatorConstant, getDiscriminatorName } from '../../util/discriminator';
import { getOneOfOptionTitle, getWidgetTitle } from '../../util/widget';
import SchemaField from '../schema-field';
import ArrayFieldItem from './array-field-item';
import DropTarget from './drop-target';

function isSingle(schema) {
  return schema.get('minItems') === 1 && schema.get('maxItems') === 1;
}

const defaultValues = {
  string: '',
  boolean: false,
  integer: 0,
  number: 0,
  object: {},
  array: []
};

function getDefaultValue(schema) {
  const defaultValue = schema.get('default');
  return defaultValue === undefined ? defaultValues[schema.get('type')] : defaultValue;
}

function initialize({ initializeArray, schema, config, formName, path }) {
  const collapseByDefault = Boolean(config.get('collapseByDefault'));
  if (!isSingle(schema)) {
    initializeArray(formName, path, { collapseByDefault });
  }
}

const ExpandCollapseIconButton = styled(IconButton)`
  svg {
    width: 20px;
    height: 20px;
  }
`;

export default class ArrayField extends PureComponent {
  static propTypes = {
    resolveRef: PropTypes.func.isRequired,
    schema: PropTypes.object.isRequired,
    schemaPath: PropTypes.array.isRequired,
    formName: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    config: PropTypes.object.isRequired,
    definitions: PropTypes.object,
    forms: PropTypes.object,
    context: PropTypes.object,
    widgets: PropTypes.object,
    error: PropTypes.string,
    isRequired: PropTypes.bool,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    locale: PropTypes.string,

    length: PropTypes.number.isRequired,
    ui: PropTypes.object.isRequired,
    fieldAction: PropTypes.func.isRequired,
    addArrayItem: PropTypes.func.isRequired,
    removeArrayItem: PropTypes.func.isRequired,
    toggleArrayItem: PropTypes.func.isRequired,
    expandAllArrayItems: PropTypes.func.isRequired,
    collapseAllArrayItems: PropTypes.func.isRequired,
    initializeArray: PropTypes.func.isRequired,
    blurField: PropTypes.func
  };

  state = {};

  constructor() {
    super();
    this.handleClickOneOfButton = this.handleClickOneOfButton.bind(this);
    this.handleCloseOneOfButton = this.handleCloseOneOfButton.bind(this);
  }

  componentDidMount() {
    initialize(this.props);
  }

  componentDidUpdate(prevProps) {
    if (this.props.path !== prevProps.path) {
      initialize(this.props);
    }
  }

  handleAddItem = (event, refSchema, discriminatorName) => {
    event.preventDefault();
    const itemSchema = this.props.resolveRef(refSchema);
    let defaultValue = getDefaultValue(itemSchema);

    if (typeof defaultValue === 'object' && discriminatorName) {
      defaultValue = {
        ...defaultValue,
        [discriminatorName]: getDiscriminatorConstant(itemSchema, discriminatorName)
      };
    }

    this.props.addArrayItem(defaultValue);
    this.props.blurField();
  };

  renderSingle(itemSchema, itemSchemaPath, itemConfig) {
    const { resolveRef, formName, context, widgets, path, disabled, readOnly, locale, forms } = this.props;
    return (
      <SchemaField
        resolveRef={resolveRef}
        schema={itemSchema}
        schemaPath={itemSchemaPath}
        formName={formName}
        path={`${path}[0]`}
        config={itemConfig}
        context={context}
        widgets={widgets}
        disabled={disabled}
        readOnly={readOnly}
        locale={locale}
        forms={forms}
      />
    );
  }

  renderFields(placeholder, { itemSchema, itemSchemaPath, itemConfig }) {
    const {
      resolveRef,
      formName,
      context,
      widgets,
      path,
      ui,
      length,
      removeArrayItem,
      toggleArrayItem,
      fieldAction,
      blurField,
      disabled,
      readOnly,
      locale,
      forms
    } = this.props;

    const keys = ui.get('arrayKeys');
    const collapsed = ui.get('collapsed');

    const totalLength = length + (placeholder ? 1 : 0);
    const fields = new Array(totalLength);

    for (let i = 0, j = 0; j < totalLength; j++) {
      const isPlaceholder = Boolean(placeholder && placeholder.get('index') === j);
      const key = isPlaceholder ? PLACEHOLDER_KEY : keys.get(i, i);

      fields[j] = (
        <ArrayFieldItem
          resolveRef={resolveRef}
          key={key}
          arrayKey={key}
          isCollapsed={collapsed.has(key)}
          index={i}
          hoverIndex={j}
          formName={formName}
          path={path}
          schema={itemSchema}
          schemaPath={itemSchemaPath}
          config={itemConfig}
          context={context}
          widgets={widgets}
          fieldAction={fieldAction}
          removeItem={removeArrayItem}
          toggleItem={toggleArrayItem}
          onBlur={blurField}
          isPlaceholder={isPlaceholder}
          disabled={disabled}
          readOnly={readOnly}
          locale={locale}
          forms={forms}
        />
      );
      i += isPlaceholder ? 0 : 1;
    }

    return fields;
  }

  getWrapperProps(Wrapper) {
    const { length } = this.props;
    const onExpandAll = this.props.expandAllArrayItems;
    const onCollapseAll = this.props.collapseAllArrayItems;

    if (Wrapper === Widget) {
      if (length > 0) {
        const actions = (
          <span>
            <ExpandCollapseIconButton onClick={onExpandAll} size="large">
              <UnfoldMoreIcon />
            </ExpandCollapseIconButton>
            <ExpandCollapseIconButton onClick={onCollapseAll} size="large">
              <UnfoldLessIcon />
            </ExpandCollapseIconButton>
          </span>
        );
        return { actions };
      }

      return {};
    }

    return { onExpandAll, onCollapseAll };
  }

  getFieldProps(dragAndDrop, placeholder) {
    const { config, path, length, fieldAction } = this.props;
    const fieldsProps = {
      className: cx(config.get('listStyle', 'mb-3'))
    };
    if (dragAndDrop) {
      fieldsProps.path = path;
      fieldsProps.count = length;
      fieldsProps.fieldAction = fieldAction;
      fieldsProps.placeholderIndex = placeholder?.get('index');
    }

    return fieldsProps;
  }

  handleClickOneOfButton(event) {
    this.setState({ anchorEl: event.currentTarget });
  }

  handleCloseOneOfButton() {
    this.setState({ anchorEl: null });
  }

  renderAddButton() {
    const { resolveRef, formName, path, schema } = this.props;
    const { anchorEl } = this.state;
    const itemSchema = resolveRef(schema.get('items'));
    const oneOf = itemSchema.get('oneOf');
    const discriminatorName = getDiscriminatorName(itemSchema);
    const addButtonProps = {
      'data-testid': `${formName}-${path}-add`,
      variant: 'contained',
      size: 'small',
      startIcon: <AddIcon />
    };
    const title = getWidgetTitle(this.props);
    const label = `Add ${title ? singular(title) : 'Item'}`;

    if (!oneOf || oneOf.size === 1) {
      return (
        <Button
          {...addButtonProps}
          onClick={(e) => {
            this.handleAddItem(e, oneOf ? oneOf.get(0) : itemSchema, discriminatorName);
          }}
        >
          {label}
        </Button>
      );
    }

    return (
      <>
        <Button {...addButtonProps} onClick={this.handleClickOneOfButton} sx={{ width: 'fit-content' }}>
          {label}
        </Button>
        <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={this.handleCloseOneOfButton}>
          {oneOf.map((one) => {
            const ref = one.get('$ref');
            const label = getOneOfOptionTitle({ schema: resolveRef(one), forms: this.props.forms }, ref);
            return (
              <MenuItem
                key={ref}
                onClick={(event) => {
                  this.setState({ anchorEl: null });
                  this.handleAddItem(event, one, discriminatorName);
                }}
              >
                {label}
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  }

  render() {
    const { schema, config, ui, widgets, disabled, readOnly, schemaPath } = this.props;

    const itemSchema = schema.get('items');

    const itemSchemaPath = [...schemaPath, 'items'];
    // TODO falling back on the parent config to support legacy form configs
    // until https://github.com/takeshape/takeshape/issues/1178 is resolved
    const itemConfig = config.get('items', config);

    if (isSingle(schema)) {
      return this.renderSingle(itemSchema, itemSchemaPath, itemConfig);
    }

    const placeholder = ui.get('placeholder');
    const dragAndDrop = config.get('dragAndDrop', false);
    const showAddButton = config.get('showAddButton', true);
    const WidgetWrapper = widgets?.[config.get('wrapper')] || Widget;

    const Fields = dragAndDrop ? DropTarget : 'div';

    return (
      <WidgetWrapper {...this.props} {...this.getWrapperProps(WidgetWrapper)}>
        <Fields {...this.getFieldProps(dragAndDrop, placeholder)}>
          {this.renderFields(placeholder, {
            itemSchema,
            itemSchemaPath,
            itemConfig
          })}
        </Fields>
        {showAddButton && !disabled && !readOnly && this.renderAddButton()}
      </WidgetWrapper>
    );
  }
}
